// ...existing code...
import { getConditionAlert, getProjectStatusAlert, formatCost, getConditionLabel, escapeHtml } from './utils.js';
import { initializeMap } from './map.js';
import { loadSchoolAndProjectData } from './data.js';
import { getPopupContent, showSidePanel } from './map-ui.js';
import { initSearch } from './search.js';
import { initDropdowns } from './dropdown.js';

// Temporary debug wrapper: intercept L.Layer.prototype.addTo to log invalid 'map' args
if (window.L && L.Layer && L.Layer.prototype && !L.Layer.prototype.__dbg_wrapped) {
  (function () {
    const origAddTo = L.Layer.prototype.addTo;
    L.Layer.prototype.__dbg_wrapped = true;
    L.Layer.prototype.addTo = function (mapArg) {
      const valid = mapArg && (typeof mapArg.addLayer === 'function' || mapArg instanceof L.Map || mapArg instanceof L.LayerGroup);
      if (!valid) {
          // invalid mapArg – no logging per user preference; return safe no-op
          return this;
        }
      return origAddTo.call(this, mapArg);
    };
  })();
}



document.addEventListener('DOMContentLoaded', async function () {
  // Delegated event listener for details-button in popup
  // Use document as the listener target because Leaflet popups are appended outside the map container
  document.addEventListener('click', async function (e) {
    try {
      // Use closest to handle clicks on inner elements inside the button
      const btn = e.target && (typeof e.target.closest === 'function' ? e.target.closest('.details-button') : (e.target.classList && e.target.classList.contains('details-button') ? e.target : null));
      if (btn) {
        const schoolName = decodeURIComponent(btn.getAttribute('data-school'));
        const school = window.schoolRows && window.schoolRows.find(s => (s["School Name"] ?? s["Campus/School"])?.toString().trim() === schoolName);
        const projectsForSchool = (window.projectRows || []).filter(p => (p["Campus"] ?? p["School Name"] ?? p["Campus/School"])?.toString().trim() === schoolName);
        try {
          const { showSidePanel } = await import('./map-ui.js');
          // open side panel without re-rendering the filter charts
          showSidePanel(school, projectsForSchool, schoolName, { renderFilterCharts: false });
          try { document.title = `${schoolName} — Cumberland County Schools Details`; } catch (e) { }
        } catch (e) { /* ignore */ }
        // Close the popup after opening side panel
        try {
          if (window.leafletMap && window.leafletMap.closePopup) window.leafletMap.closePopup();
          else document.querySelectorAll('.leaflet-popup-close-button').forEach(b => { try { b.click(); } catch(e){} });
        } catch (e) { /* ignore */ }
      }
    } catch (err) { /* ignore */ }
  });
  // Global focusin handler: if focus moves into a leaflet marker or popup, clear the search UI.
  // This duplicates a small part of the map's clearSearchUI logic so it reliably runs
  // even if the map's internal handlers execute in a different order.
  try {
    document.addEventListener('focusin', function (ev) {
      try {
        const tgt = ev && ev.target;
        if (!tgt) return;
        const isMarker = (typeof tgt.closest === 'function') ? !!tgt.closest('.leaflet-marker-icon') : false;
        const isPopup = (typeof tgt.closest === 'function') ? !!tgt.closest('.leaflet-popup') : false;
        if (!isMarker && !isPopup) return;
        // Clear search input and UI
        try {
          const searchInput = document.getElementById('schoolSearchInput');
          const searchContainer = document.querySelector('.search-container');
          const searchList = document.getElementById('schoolSearchList');
          const emptyEl = document.getElementById('schoolSearchEmpty');
          const toggleBtn = document.getElementById('searchToggleBtn');
          if (searchInput) {
            try { searchInput.value = ''; } catch (e) {}
            try { searchInput.dispatchEvent(new Event('input', { bubbles: true })); } catch (e) {}
            try { searchInput.blur(); } catch (e) {}
          }
          if (searchContainer) {
            try { searchContainer.classList.remove('has-value'); } catch (e) {}
            try { searchContainer.classList.remove('show-list'); } catch (e) {}
            try { searchContainer.classList.remove('show-empty'); } catch (e) {}
            try { searchContainer.classList.remove('expanded'); } catch (e) {}
          }
          if (searchList) try { searchList.innerHTML = ''; } catch (e) {}
          if (emptyEl) try { emptyEl.removeAttribute('style'); } catch (e) {}
          if (toggleBtn) try { toggleBtn.setAttribute('aria-expanded', 'false'); } catch (e) {}
          // Do not clear __openedFromSearch here; preserve it until Back consumes it
        } catch (e) { /* ignore */ }
      } catch (e) { /* ignore */ }
    });
  } catch (e) { /* ignore */ }
  // Keyboard support: when a Leaflet popup container is focused, Enter should activate
  // the popup's primary action (the .details-button). This forwards Enter/Space from
  // the popup container to the button so keyboard users can press Enter while the
  // popup has focus and open the side panel.
  try {
    document.addEventListener('keydown', function (ev) {
      try {
        const k = ev && (ev.key || ev.keyCode);
        const isEnter = k === 'Enter' || k === ' ' || k === 'Spacebar' || k === 13 || k === 32;
        if (!isEnter) return;
        const active = document.activeElement;
        if (!active) return;
        // If the focused element is the popup's close button (or inside it),
        // allow the default behavior so the popup simply closes and do NOT
        // forward the Enter key to the popup's .details-button which would
        // otherwise open the side panel.
        const closeBtn = (typeof active.closest === 'function') ? active.closest('.leaflet-popup-close-button') : null;
        if (closeBtn) {
          // Let the close button perform its normal action (do not preventDefault)
          return;
        }
        // If focused element is the popup container itself or a child inside it,
        // forward the key press to the details-button inside the same popup
        const popup = (typeof active.closest === 'function') ? active.closest('.leaflet-popup') : null;
        if (!popup) return;
        const btn = popup.querySelector('.details-button');
        if (!btn) return;
        try { ev.preventDefault && ev.preventDefault(); } catch (e) {}
        try { btn.click(); } catch (e) { try { btn.dispatchEvent(new MouseEvent('click', { bubbles: true })); } catch (e2) {} }
      } catch (e) { /* ignore */ }
    });
  } catch (e) { /* ignore */ }
  // When a Leaflet popup close button is clicked, restore the All Schools filter panel and show filters
  document.addEventListener('click', function (e) {
    try {
      const closeBtn = e.target && (typeof e.target.closest === 'function' ? e.target.closest('.leaflet-popup-close-button') : null);
      if (!closeBtn) return;
      // After the popup close button is clicked, focus should return to the
      // previously-clicked marker (if available). Do this gently and do NOT
      // modify any other state or call side-panel functions.
      try {
        // If the popup was opened from a search result, restore focus to the
        // search input so keyboard users can continue navigating search results.
        const openedFromSearch = (typeof window !== 'undefined') ? !!window.__openedFromSearch : false;
        if (openedFromSearch) {
          try {
            const searchInput = document.getElementById('schoolSearchInput');
            if (searchInput) {
              try { searchInput.focus({ preventScroll: false }); } catch (e) { try { searchInput.focus(); } catch (e2) {} }
              // Clear the flag; Back or other handlers will control further state
              try { delete window.__openedFromSearch; } catch (e) { window.__openedFromSearch = false; }
              return;
            }
          } catch (siErr) { /* ignore search focus errors and fall back to marker */ }
        }
        // Fallback: focus last-clicked marker as previous behavior
        const lastMarker = (typeof window !== 'undefined') ? window.__lastClickedMarker : null;
        if (lastMarker && typeof lastMarker.getElement === 'function') {
          const mEl = lastMarker.getElement();
          if (mEl) {
            try { if (!mEl.hasAttribute || !mEl.hasAttribute('tabindex')) { try { mEl.setAttribute('tabindex', '0'); } catch (e) {} } } catch (e) {}
            try { mEl.focus({ preventScroll: false }); } catch (e) { try { mEl.focus(); } catch (e2) {} }
          }
        }
      } catch (focusErr) { /* ignore focus errors */ }
      // Intentionally do nothing else so closing a popup preserves the currently
      // displayed side-panel (e.g., the selected school's data) and does not
      // automatically switch back to 'All Schools' or modify filters or map view.
      // If this click was initiated from the keyboard (Enter), reset charts
      // to the All Schools state so keyboard users get a full reset. We
      // detect keyboard activation heuristically: Keyboard-initiated clicks
      // often have event.detail === 0 and are instances of KeyboardEvent.
      try {
        const isKeyboardActivation = (typeof e === 'object' && e && ((e instanceof KeyboardEvent) || e.detail === 0));
        if (isKeyboardActivation) {
          try { if (typeof showSidePanel === 'function') showSidePanel(null, window.projectRows || [], 'All Schools'); } catch (ssErr) { }
          try { if (typeof window.setFilterActive === 'function') window.setFilterActive(false); else window.__filterActive = false; } catch (sfErr) { }
        }
      } catch (detErr) { /* ignore */ }
      return;
    } catch (err) { /* ignore */ }
  });
  // Dynamically populate metric select box
  const metricOptions = [
    'Facility 7-Year Plan Cost',
    'Condition Score',
    'Suitability Score',
    'Tech Readiness Score',
    'Combined Score'
  ];
  const metricSelect = document.querySelector('.filter-select');
  if (metricSelect) {
    metricSelect.innerHTML = metricOptions.map(opt => `<option>${escapeHtml(opt)}</option>`).join('');
  }
  const typeBtns = document.querySelectorAll('.school-type-btn');
  let selectedType = null;
  typeBtns.forEach(b => b.classList.remove('active'));
  selectedType = null;
  // Expose selectedType so other modules (like back button) can check current filter
  try { window.__selectedSchoolType = selectedType; } catch (e) {}
  // Reset button (hidden by default until a filter is applied)
  const clearBtn = document.getElementById('clearSchoolFilterBtn');
  try { if (clearBtn) clearBtn.style.display = 'none'; } catch (e) {}
  // Track whether a user-applied filter (type, nearby, marker click) is active.
  // This helps keep the Reset button visible until the user explicitly clears it.
  try {
    window.setFilterActive = function (v) {
      try { window.__filterActive = !!v; } catch (e) {}
      try {
        const cb = document.getElementById('clearSchoolFilterBtn');
        if (cb) cb.style.display = (!!v) ? '' : 'none';
      } catch (e) {}
    };
    window.setFilterActive(false);
  } catch (e) {}

  // Helper to clear the school-type filter UI and state
  function clearSchoolTypeFilter() {
    try {
      const buttons = document.querySelectorAll('.school-type-btn');
      buttons.forEach(b => b.classList.remove('active'));
    } catch (e) {}
    selectedType = null;
    try { window.__selectedSchoolType = null; } catch (e) {}
    try { if (typeof window.setFilterActive === 'function') window.setFilterActive(false); else window.__filterActive = false; } catch (e) {}
    try { if (clearBtn) clearBtn.style.display = 'none'; } catch (e) {}
  }
  // expose so map.js and other modules can reset the filter when needed
  try { window.clearSchoolTypeFilter = clearSchoolTypeFilter; } catch (e) {}

  // Load data and initialize map
  const { schoolRows, projectRows } = await loadSchoolAndProjectData();
  window.schoolRows = schoolRows;
  // Build a quick lookup of schools by legislative district for debugging and fast filtering
  try {
    const buildSchoolsByDistrict = () => {
      const map = new Map();
      (window.schoolRows || []).forEach(s => {
        try {
          const raw = s["Legislative District"] ?? s["LegislativeDistrict"] ?? '';
          const ldStr = (raw == null) ? '' : String(raw).trim();
          const ldNum = Number(ldStr);
          const key = Number.isFinite(ldNum) ? ldNum : ldStr;
          const name = String(s["School Name"] ?? s["Campus/School"] ?? '').trim();
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(name);
        } catch (e) { /* ignore row */ }
      });
      try { window.__schoolsByLegDistrict = map; } catch (e) {}
  try { window.logSchoolsByDistrict = () => { return Array.from((window.__schoolsByLegDistrict || new Map()).entries()); }; } catch(e){}
  // debug logging removed
    };
    buildSchoolsByDistrict();
  } catch (e) { /* ignore */ }
  // Helper to inspect matched schools for a given district from runtime processing
  try {
    window.getSchoolsInDistrict = function (d) {
      try {
        const normalize = (v) => (v == null) ? '' : String(v).trim().toLowerCase();
        const res = [];
        const sel = (d == null) ? null : String(d).trim();
        if (!sel) return res;
        const selNum = Number(sel);
        (window.schoolRows || []).forEach(s => {
          try {
            const raw = s["Legislative District"] ?? s["LegislativeDistrict"] ?? '';
            const ldStr = (raw == null) ? '' : String(raw).trim();
            const ldNum = Number(ldStr);
            const matches = (Number.isFinite(selNum) && Number.isFinite(ldNum)) ? (selNum === ldNum) : (String(ldStr) === sel);
            if (matches) res.push(String(s["School Name"] ?? s["Campus/School"]).trim());
          } catch (e) { }
        });
        return res;
      } catch (e) { return []; }
    };
  window.compareDistrict = function (d, expected) { try { const arr = window.getSchoolsInDistrict(d); return arr; } catch(e){ return []; } };
  } catch (e) { /* ignore */ }

  // Build normalized marker lookup (name -> marker object) for reliable matching
  try {
    const normalize = (v) => (v == null) ? '' : String(v).trim().toLowerCase();
    const markerMap = new Map();
    schoolMarkers.forEach(obj => {
      try {
        markerMap.set(normalize(obj.name), obj);
      } catch (e) { /* ignore */ }
    });
    try { window.__markerByNameNormalized = markerMap; } catch(e) {}
  } catch (e) { /* ignore */ }
  // expose projectRows globally so other modules can use them when re-rendering the filter panel
  window.projectRows = projectRows;
  let schoolMarkers = [];
  // Initialize map and show markers by default. Previously markers were suppressed until
  // geolocation permission was granted which can make the map appear empty if the
  // browser denies or blocks location requests. Show markers on init for a better UX.
  // Ensure Leaflet assets are available before calling initializeMap. This dynamically
  // loads Leaflet CSS/JS when needed which reduces render-blocking on initial load.
  // loadCss: href string, optional attrs object { integrity, crossOrigin }
  async function loadCss(href, attrs = {}) {
    return new Promise((resolve, reject) => {
      try {
        if (document.querySelector(`link[href="${href}"]`)) return resolve();
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = href;
        if (attrs.integrity) l.integrity = attrs.integrity;
        // Use 'anonymous' by default when crossorigin requested
        if (attrs.crossOrigin) l.crossOrigin = attrs.crossOrigin;
        l.onload = () => resolve();
        l.onerror = (e) => reject(e);
        document.head.appendChild(l);
      } catch (e) { reject(e); }
    });
  }

  // loadScript: src string, optional attrs object { integrity, crossOrigin }
  async function loadScript(src, attrs = {}) {
    return new Promise((resolve, reject) => {
      try {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        if (attrs.integrity) s.integrity = attrs.integrity;
        if (attrs.crossOrigin) s.crossOrigin = attrs.crossOrigin;
        s.onload = () => resolve();
        s.onerror = (e) => reject(e);
        document.body.appendChild(s);
      } catch (e) { reject(e); }
    });
  }

  let _leafletEnsured = false;
  async function ensureLeaflet() {
    if (_leafletEnsured) return;
    _leafletEnsured = true;
  // Load CSS first so map renders correctly when initialized
  // Prefer local npm package files (serve from node_modules) to avoid CDN reliance
  await loadCss('/vendor/leaflet/leaflet.css');
  // Then load the JS
  await loadScript('/vendor/leaflet/leaflet.js');
    // Small delay to allow global L to initialize in some browsers
    await new Promise(r => setTimeout(r, 20));
  }

  // If the map element is in or near the viewport, load Leaflet immediately; otherwise
  // lazy-load it when the map scrolls into view.
  const mapEl = document.getElementById('map');
  if (mapEl) {
    const inViewport = () => {
      try {
        const rect = mapEl.getBoundingClientRect();
        return rect.top < (window.innerHeight || document.documentElement.clientHeight) + 400; // 400px prefetch
      } catch (e) { return true; }
    };
    if (inViewport()) await ensureLeaflet();
    else {
      // Use IntersectionObserver to load when close to viewport
      try {
        const io = new IntersectionObserver(async (entries, obs) => {
          for (const ent of entries) {
            if (ent.isIntersecting) {
              obs.disconnect();
              try { await ensureLeaflet(); } catch(e) {}
              break;
            }
          }
        }, { rootMargin: '400px' });
        io.observe(mapEl);
        // Also set a fallback timeout to ensure leaflet loads eventually
        setTimeout(() => { try { ensureLeaflet(); } catch(e) {} }, 8000);
      } catch (e) {
        // If IntersectionObserver not available, load after short delay
        setTimeout(() => { try { ensureLeaflet(); } catch(e) {} }, 250);
      }
    }
  }

  await ensureLeaflet();
  const map = initializeMap(schoolMarkers, schoolRows, projectRows, { showMarkersOnInit: true });
  window.leafletMap = map;
  // expose markers for other modules to inspect visibility
  window.schoolMarkers = schoolMarkers;

  // Disable automatic geolocation-based filtering on initial load. We do not request
  // or track the user's location. Instead, show the full district on first load
  // (map bounds are fitted to district GeoJSON later) and do not set any nearby filter.
  (function disableAutoNearby(){
    try {
      // Ensure there is no nearby filter active
      window.__nearbyInfo = null;
      // Show full All Schools summary in the filter panel
      try { showSidePanel(null, projectRows, 'All Schools'); } catch (e) {}
      // Ensure all markers are visible (initializeMap has already added markers by default)
      try { if (typeof updateSchoolMarkers === 'function') updateSchoolMarkers(null); } catch (e) {}
    } catch (e) { /* ignore */ }
  })();

  // Ensure clicking the map background restores the 'All Schools' view for the current radius
  try {
    // Previously clicking on the map background would reset filters and reload all-school data.
    // That behavior has been removed per request: clicking the map no longer triggers loading of all school data.
    // If other behavior is desired on map click, add it here.
  } catch (e) { /* ignore */ }

  // Show all-schools dashboard on load
  try {
    showSidePanel(null, projectRows, 'All Schools');
  } catch (e) {
    // suppressed logging per user request
  }

  // Focus search input on initial page load/reload.
  // Behavior:
  // - Only autofocus if the document/window appears to be focused and the user hasn't
  //   already focused another control (to avoid stealing focus during interaction).
  // - Expand the search UI so the input is visible when focused.
  // - Use a short timeout to allow initial DOM inserts (offcanvas, filters) to complete.
  try {
    (function focusSearchInputOnLoad() {
      try {
        const inputId = 'schoolSearchInput';
        const si = document.getElementById(inputId);
        if (!si) return;
        // If the user has already focused something else, do not override.
        const alreadyInteracted = (document.activeElement && document.activeElement !== document.body && document.activeElement !== document.documentElement && document.activeElement !== si);
        // Only proceed when the window is focused (prevents background-tab steals).
        const winHasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        if (!winHasFocus || alreadyInteracted) return;

        setTimeout(() => {
          try {
            // Expand the search container so the input is visible (matches other code that toggles classes)
            // Do not automatically show the suggestion list on load — only expand input and set aria state.
            const sc = document.querySelector('.search-container');
            if (sc) {
              sc.classList.add('expanded');
              // Intentionally do NOT add 'show-list' here to avoid showing suggestions until user types or focuses.
            }
            const tb = document.getElementById('searchToggleBtn');
            if (tb) tb.setAttribute('aria-expanded', 'true');
            try { si.focus({ preventScroll: false }); } catch (e) { try { si.focus(); } catch (e) {} }
          } catch (e) { /* ignore focus errors */ }
        }, 50);
      } catch (e) { /* ignore */ }
    })();
  } catch (e) { /* ignore */ }

  // Wire the Clear Filter button to reset type filters, markers and reload All-Schools summary
  try {
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        try {
          // Clear UI and state for type filters
          try { clearSchoolTypeFilter(); } catch (e) {}
          // Also clear any legislative district selection and UI
          try {
            const legToggle = document.querySelector('.legislative-district-toggle');
            if (legToggle) {
              const db = Array.from(legToggle.querySelectorAll('.district-btn'));
              db.forEach(b => { try { b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); } catch(e){} });
            }
            try { window.__selectedLegislativeDistrict = null; } catch(e){}
            try { if (typeof window.updateSchoolMarkersByDistrict === 'function') window.updateSchoolMarkersByDistrict(null); } catch(e){}
          } catch (e) { /* ignore */ }
          // Reset markers to show all (honor nearby radius if present)
          try { if (typeof updateSchoolMarkers === 'function') updateSchoolMarkers(null); } catch (e) {}
          // Reload the filter panel to All Schools
          try { showSidePanel(null, projectRows, 'All Schools'); } catch (e) {}
          // Close any open leaflet popup to match initial first-load state
          try {
            if (window.leafletMap && typeof window.leafletMap.closePopup === 'function') {
              window.leafletMap.closePopup();
            } else {
              // DOM fallback: click any close buttons
              document.querySelectorAll('.leaflet-popup-close-button').forEach(btn => { try { btn.click(); } catch(e){} });
            }
          } catch (e) { /* ignore */ }
          // Restore initial map view/bounds so the map returns to first-load position
          try {
            const duration = 0.9;
            if (window.__initialView && window.leafletMap) {
              const v = window.__initialView;
              try {
                // Pan to initial center preserving current zoom
                if (typeof window.leafletMap.panTo === 'function') {
                  window.leafletMap.panTo(v.center, { animate: true, duration });
                } else if (typeof window.leafletMap.setView === 'function') {
                  const currentZoom = (window.leafletMap.getZoom && typeof window.leafletMap.getZoom === 'function') ? window.leafletMap.getZoom() : v.zoom;
                  try { window.leafletMap.setView(v.center, currentZoom, { animate: true }); } catch(e) { window.leafletMap.setView(v.center, v.zoom); }
                }
              } catch (e) { /* ignore */ }
            } else if (window.__initialBounds && window.leafletMap && typeof window.leafletMap.fitBounds === 'function') {
              try {
                window.leafletMap.fitBounds(window.__initialBounds, { animate: true, padding: [20, 20] });
              } catch (e) { /* ignore */ }
            }
          } catch (e) { /* ignore */ }
          // hide the reset button after clearing
          try { if (typeof window.setFilterActive === 'function') window.setFilterActive(false); else { window.__filterActive = false; if (clearBtn) try { clearBtn.style.display = 'none'; } catch(e){} } } catch (e) {}
          // Reset HS attendance dropdown label only (do not hide or clear the menu so testing can proceed)
          try {
            const hsDropdown = document.querySelector('.hs-attendance-area-dropdown');
            if (hsDropdown) {
              try {
                const toggle = hsDropdown.querySelector('.dropdown-toggle');
                if (toggle) {
                  try { toggle.removeAttribute('data-selected'); } catch(e) {}
                  try { toggle.classList.remove('active'); } catch(e) {}
                  try { Array.from(toggle.childNodes).forEach(n => { if (n.nodeType === Node.TEXT_NODE) toggle.removeChild(n); }); } catch(e) {}
                  try { toggle.insertBefore(document.createTextNode('Select School'), toggle.firstChild); } catch(e) {}
                }
              } catch(e) {}
            }
          } catch(e) {}
          // Also clear any active state on dropdown menu items and reset dropdown toggles
          try {
            // Remove active class from any selected dropdown items
            document.querySelectorAll('.dropdown-menu .dropdown-item.active').forEach(it => { try { it.classList.remove('active'); it.removeAttribute('aria-pressed'); } catch(e){} });
            // Remove data-selected and active class from toggles
            document.querySelectorAll('.dropdown-toggle').forEach(t => { try { t.removeAttribute('data-selected'); t.classList.remove('active'); } catch(e){} });
          } catch(e) {}
          // Clear the search input and ensure the suggestion list is hidden
          try {
            const si = document.getElementById('schoolSearchInput');
            if (si) {
              si.value = '';
              // dispatch an input event so the search module updates its list
              try { si.dispatchEvent(new Event('input', { bubbles: true })); } catch(e) { /* ignore */ }
            }
          } catch (e) { /* ignore */ }
          // Also collapse the expandable search UI (remove expanded state)
          try {
            const sc = document.querySelector('.search-container');
            const tb = document.getElementById('searchToggleBtn');
            const si = document.getElementById('schoolSearchInput');
            if (sc) {
              sc.classList.remove('expanded');
              sc.classList.remove('show-list');
            }
            if (tb) tb.setAttribute('aria-expanded', 'false');
            if (si) try { si.blur(); } catch(e) {}
          } catch (e) { /* ignore */ }
        } catch (e) { /* ignore */ }
      });
    }
  } catch (e) {}

  // Ensure the map filter panel refreshes when the side panel is closed (anyway it is closed)
  try {
    const sidePanelEl = document.getElementById('sidePanel');
    // helper: detect whether any map-level school filter is active
    function isSchoolFilterActive() {
      try {
        // type filter (buttons) active?
        if (document.querySelector('.school-type-btn.active')) return true;
        // nearby radius active?
        if (window.__nearbyInfo && window.__nearbyInfo.lat != null) return true;
        return false;
      } catch (e) { return false; }
    }

    // helper: call showSidePanel but avoid duplicated rapid calls; also avoid reloading All Schools
    // when the user has an active school filter (type or nearby radius)
    function refreshFilterPanel() {
      try {
        if (isSchoolFilterActive()) return; // user has an active filter -> don't reload All Schools
        if (window.__filterRefreshLock) return; // already scheduled
        window.__filterRefreshLock = true;
        try { showSidePanel(null, window.projectRows || [], 'All Schools'); } catch (e) {}
        // clear lock after a short delay to allow future legitimate refreshes
        setTimeout(() => { try { window.__filterRefreshLock = false; } catch (e) {} }, 700);
      } catch (e) {}
    }
    // Do not auto-refresh the All-Schools summary when the side panel is closed.
    // Previously closing the panel would trigger a reload of the All-Schools filter
    // which caused charts and summaries to reset unexpectedly. We only refresh
    // the filter panel when the user explicitly clicks Reset.
    // If you want an explicit restore-on-close behavior in future, add it here.
    // (no event listener attached)
    if (sidePanelEl && window.bootstrap && window.bootstrap.Offcanvas) {
      /* intentionally left blank to avoid auto-refresh on close */
    }
  } catch (e) {}

  // Geo boundary layer removed per user request.

  // Toggle legend open/close when slider handle is clicked
  try {
    const slider = document.querySelector('.slider-handle');
    const legend = document.querySelector('.school-type-legend');
    if (slider && legend) {
      // reflect initial state
      const isInitiallyOpen = legend.classList.contains('open');
      slider.setAttribute('aria-expanded', isInitiallyOpen ? 'true' : 'false');
      // If using Material Symbols (ligatures), ensure the icon text matches initial state
      try {
        const msIcon = slider.querySelector('.material-symbols-outlined');
        if (msIcon) {
          // show backward arrow when closed (pointing left), forward when open
          msIcon.textContent = isInitiallyOpen ? 'arrow_forward_ios' : 'arrow_back_ios';
        }
      } catch (e) { /* ignore icon init errors */ }
      slider.addEventListener('click', function (ev) {
        try {
          const isOpen = legend.classList.toggle('open');
          slider.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          // toggle chevron direction for Material Symbols (swap ligature text)
          try {
            const msIcon = slider.querySelector('.material-symbols-outlined');
            if (msIcon) {
              msIcon.textContent = isOpen ? 'arrow_forward_ios' : 'arrow_back_ios';
            }
          } catch (e) { /* ignore icon swap errors */ }
        } catch (e) { /* ignore click handler errors */ }
      }, { passive: true });
    }
  } catch (e) {
    // suppressed logging per user request
  }

  // Reveal Material Symbols only after the font has loaded to avoid showing
  // the ligature text (e.g. "arrow_left_alt") on hard loads. Uses the
  // Font Loading API when available, with a short timeout fallback.
  try {
    (function revealMaterialSymbols() {
      const applyReady = () => {
        try { document.documentElement.classList.add('msf-ready'); } catch (e) {}
        // Initialize Bootstrap tooltips for any elements that declare them (legend items, etc.)
        try {
          if (window.bootstrap && window.bootstrap.Tooltip) {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.forEach(function (el) {
              try { new bootstrap.Tooltip(el); } catch (e) {}
            });
          }
        } catch (e) {}
      };
      if (document.fonts && typeof document.fonts.load === 'function') {
        // Request the material symbols font family; this will resolve when the
        // font is available and rendered.
        // Use a lightweight descriptor that most browsers support.
        try {
          const p = document.fonts.load('1rem "Material Symbols Outlined"');
          // If the load promise resolves within a reasonable time, reveal icons.
          const t = setTimeout(() => applyReady(), 1500);
          p.then(() => { clearTimeout(t); applyReady(); }).catch(() => { /* ignore */ applyReady(); });
        } catch (e) {
          // if anything goes wrong, reveal after a small delay
          setTimeout(applyReady, 250);
        }
      } else {
        // No Font Loading API - reveal after a short timeout to avoid long hide
        setTimeout(applyReady, 250);
      }
    })();
  } catch (e) { /* ignore font loading errors */ }

  function updateSchoolMarkers(type) {
    schoolMarkers.forEach(obj => {
      if (!(obj.marker instanceof L.Marker)) {
        return;
      }
      if (!window.leafletMap || !(window.leafletMap instanceof L.Map)) {
        return;
      }
      // Normalize the requested type and the marker's types so we can
      // reliably match and pick the correct color even when the data
      // contains variations like "Middle/High", full words, or codes.
      const normalizeTypeCode = (v) => {
        try {
          if (v == null) return null;
          const s = String(v).trim().toLowerCase();
          if (!s) return null;
          if (s === 'es' || s === 'elementary' || s.includes('elementary')) return 'ES';
          if (s === 'ms' || s === 'middle' || s.includes('middle')) return 'MS';
          if (s === 'hs' || s === 'high' || s.includes('high')) return 'HS';
          return s.toUpperCase();
        } catch (e) { return null; }
      };
      const selType = normalizeTypeCode(type);
      // Build a normalized types array from obj.types (which may be an array or a slash/comma separated string)
      let normalizedTypes = [];
      try {
        if (obj.types) {
          if (Array.isArray(obj.types)) {
            normalizedTypes = obj.types.map(t => normalizeTypeCode(t)).filter(Boolean);
          } else {
            // split on common separators if it's a string like "Middle/High School"
            normalizedTypes = String(obj.types).split(/[,/|;]+/).map(t => normalizeTypeCode(t)).filter(Boolean);
          }
        }
        // If obj.type looks like a primary code, include it as a fallback
        const primary = normalizeTypeCode(obj.type);
        if (primary && normalizedTypes.indexOf(primary) === -1) normalizedTypes.unshift(primary);
      } catch (e) { /* ignore normalization errors */ }
      // Respect both the requested type filter and the current nearby radius (if present)
      // For combined-type schools (e.g., 'Middle/High School'), check if the requested type
      // exists in the school's normalized types array to show it in multiple category filters
      let matchesType = !selType || (obj.type && normalizeTypeCode(obj.type) === selType);
      if (!matchesType && selType && normalizedTypes.length) {
        matchesType = normalizedTypes.includes(selType);
      }
      
      let withinNearby = true;
      try {
        const ni = window.__nearbyInfo;
        if (ni && ni.lat != null && ni.lng != null && ni.RADIUS_KM != null) {
          const mLatLng = obj.marker.getLatLng();
          // haversine distance in km
          const toRad = (d) => d * Math.PI / 180;
          const R = 6371;
          const dLat = toRad(mLatLng.lat - ni.lat);
          const dLon = toRad(mLatLng.lng - ni.lng);
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(toRad(ni.lat)) * Math.cos(toRad(mLatLng.lat)) * Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          const dkm = R * c;
          withinNearby = dkm <= ni.RADIUS_KM;
        }
      } catch (e) { /* ignore and assume withinNearby = true */ }

      if (matchesType && withinNearby) {
        // Ensure the marker is added first so its DOM element exists when we try
        // to update the dot color. Adding before color update avoids the case
        // where a marker appears for the first time with the wrong color.
        if (!window.leafletMap.hasLayer(obj.marker)) obj.marker.addTo(window.leafletMap);

        // Helper to apply a color to the marker dot; if the element isn't
        // available yet, retry once shortly after (minimal delay).
        const applyDotColor = (marker, color) => {
          try {
            const markerEl = marker.getElement();
            if (markerEl) {
              const dot = markerEl.querySelector('.marker-dot');
              if (dot) dot.style.background = color;
              return;
            }
          } catch (e) { /* ignore */ }
          // Retry once after a short delay
          try {
            setTimeout(() => {
              try {
                const markerEl2 = marker.getElement();
                if (markerEl2) {
                  const dot2 = markerEl2.querySelector('.marker-dot');
                  if (dot2) dot2.style.background = color;
                }
              } catch (e) { /* ignore */ }
            }, 20);
          } catch (e) { /* ignore timers errors in exotic envs */ }
        };

        // Update marker color based on active filter for multi-type schools
        if (selType && normalizedTypes.length > 1) {
          try {
            let newColor = '#999'; // default
            if (selType === 'ES') newColor = '#36438B'; // Elementary blue
            else if (selType === 'MS') newColor = '#FF5E3C'; // Middle orange
            else if (selType === 'HS') newColor = '#DCDC19'; // High yellow
            applyDotColor(obj.marker, newColor);
          } catch (e) { /* ignore color update errors */ }
        } else if (!type && obj.marker) {
          // Reset to primary type color when no filter is active
          try {
            let defaultColor = '#999';
            const prim = normalizeTypeCode(obj.type);
            if (prim === 'ES') defaultColor = '#36438B';
            else if (prim === 'MS') defaultColor = '#FF5E3C';
            else if (prim === 'HS') defaultColor = '#DCDC19';
            applyDotColor(obj.marker, defaultColor);
          } catch (e) { /* ignore color reset errors */ }
        }
      } else {
        if (window.leafletMap.hasLayer(obj.marker)) window.leafletMap.removeLayer(obj.marker);
      }
    });
  }
  // Expose for other modules (map.js) so clicking the map can reset filters to 'all types within radius'
  try { window.updateSchoolMarkers = updateSchoolMarkers; } catch(e) { /* ignore */ }

  // Support filtering markers by Legislative District (string or null to clear)
  let selectedLegislativeDistrict = null;
  function updateSchoolMarkersByDistrict(district) {
    try {
      selectedLegislativeDistrict = (district == null) ? null : String(district).trim();
      try { window.__selectedLegislativeDistrict = selectedLegislativeDistrict; } catch(e) {}
  // debug logging removed
  // Debug logging disabled by default. Set window.__debugDistrictFiltering = true to enable.
      // Compute the set of normalized school names that belong to the selected district
      const normalize = (v) => (v == null) ? '' : String(v).trim().toLowerCase();
      const schoolsInDistrict = new Set();
      if (selectedLegislativeDistrict) {
        try {
          const selNum = Number(String(selectedLegislativeDistrict).trim());
          (window.schoolRows || []).forEach(s => {
            try {
              const raw = s["Legislative District"] ?? s["LegislativeDistrict"] ?? '';
              const ldStr = (raw == null) ? '' : String(raw).trim();
              const ldNum = Number(ldStr);
              const matches = (Number.isFinite(selNum) && Number.isFinite(ldNum)) ? (selNum === ldNum) : (String(ldStr) === String(selectedLegislativeDistrict));
              if (matches) {
                schoolsInDistrict.add(normalize(s["School Name"] ?? s["Campus/School"]));
              }
            } catch (e) { /* ignore row */ }
          });
        } catch (e) { /* ignore */ }
      }
  // debug logging removed
  try { if (window.__debugDistrictFiltering) { console.info && console.info('schoolsInDistrict size', schoolsInDistrict.size, 'sample', Array.from(schoolsInDistrict).slice(0,10)); } } catch(e) {}
      // Fallback: if nothing matched, try extracting digits from the raw Legislative District field
      if (selectedLegislativeDistrict && schoolsInDistrict.size === 0) {
        try {
          const selDigits = (String(selectedLegislativeDistrict).match(/\d+/) || [''])[0];
          if (selDigits) {
            (window.schoolRows || []).forEach(s => {
              try {
                const raw = s["Legislative District"] ?? s["LegislativeDistrict"] ?? '';
                const rawDigits = (String(raw).match(/\d+/) || [''])[0];
                if (rawDigits && rawDigits === selDigits) {
                  schoolsInDistrict.add(normalize(s["School Name"] ?? s["Campus/School"]));
                }
              } catch (e) { /* ignore */ }
            });
            // Debug logging disabled by default. Set window.__debugDistrictFiltering = true to enable.
          }
        } catch (e) { /* ignore */ }
      }

      // Now show/hide markers. Prefer using attached marker.legislativeDistrict when available
      schoolMarkers.forEach(obj => {
        try {
          if (!(obj.marker instanceof L.Marker)) return;
          if (!window.leafletMap || !(window.leafletMap instanceof L.Map)) return;
          // When a legislative district is selected we want to show ALL markers
          // that belong to that district regardless of other filters (type/nearby).
          // This makes district selection authoritative per user's request.
          let matchesType = !selectedType || obj.type === selectedType;
          // Enhanced matching: check types array for multi-category schools
          if (!matchesType && selectedType && obj.types && Array.isArray(obj.types)) {
            matchesType = obj.types.includes(selectedType);
          }
          
          let withinNearby = true;
          try {
            const ni = window.__nearbyInfo;
            if (ni && ni.lat != null && ni.lng != null && ni.RADIUS_KM != null) {
              const mLatLng = obj.marker.getLatLng();
              const toRad = (d) => d * Math.PI / 180;
              const R = 6371;
              const dLat = toRad(mLatLng.lat - ni.lat);
              const dLon = toRad(mLatLng.lng - ni.lng);
              const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(toRad(ni.lat)) * Math.cos(toRad(mLatLng.lat)) * Math.sin(dLon/2) * Math.sin(dLon/2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
              const dkm = R * c;
              withinNearby = dkm <= ni.RADIUS_KM;
            }
          } catch (e) { /* ignore and assume withinNearby = true */ }

          // If the marker was created with a legislativeDistrict property, use it directly
          let inDistrict = true;
          if (selectedLegislativeDistrict) {
            try {
              const markerLd = (obj.legislativeDistrict != null) ? obj.legislativeDistrict : (obj.marker && obj.marker.__legislativeDistrict != null ? obj.marker.__legislativeDistrict : null);
              if (markerLd != null) {
                const selNum = Number(String(selectedLegislativeDistrict).trim());
                if (Number.isFinite(selNum) && Number.isFinite(Number(markerLd))) {
                  inDistrict = (selNum === Number(markerLd));
                } else {
                  inDistrict = (String(markerLd) === String(selectedLegislativeDistrict));
                }
              } else {
                // fallback to name-based set membership
                inDistrict = schoolsInDistrict.has(normalize(obj.name));
              }
            } catch (e) {
              inDistrict = schoolsInDistrict.has(normalize(obj.name));
            }
            // When district is selected, ignore other filters (match by district only)
            matchesType = true;
            withinNearby = true;
          } else {
            inDistrict = true;
          }

          if (matchesType && inDistrict && withinNearby) {
            // Update marker color based on active filter for multi-type schools
            if (selectedType && obj.types && Array.isArray(obj.types) && obj.types.length > 1) {
              try {
                const markerEl = obj.marker.getElement();
                if (markerEl) {
                  const dot = markerEl.querySelector('.marker-dot');
                  if (dot) {
                    let newColor = '#999'; // default
                    if (selectedType === 'ES') newColor = '#36438B'; // Elementary blue
                    else if (selectedType === 'MS') newColor = '#FF5E3C'; // Middle orange
                    else if (selectedType === 'HS') newColor = '#DCDC19'; // High yellow
                    dot.style.background = newColor;
                  }
                }
              } catch (e) { /* ignore color update errors */ }
            } else if (!selectedType && obj.marker) {
              // Reset to primary type color when no filter is active
              try {
                const markerEl = obj.marker.getElement();
                if (markerEl) {
                  const dot = markerEl.querySelector('.marker-dot');
                  if (dot) {
                    let defaultColor = '#999';
                    if (obj.type === 'ES') defaultColor = '#36438B';
                    else if (obj.type === 'MS') defaultColor = '#FF5E3C';
                    else if (obj.type === 'HS') defaultColor = '#DCDC19';
                    dot.style.background = defaultColor;
                  }
                }
              } catch (e) { /* ignore color reset errors */ }
            }
            
            if (!window.leafletMap.hasLayer(obj.marker)) obj.marker.addTo(window.leafletMap);
          } else {
            if (window.leafletMap.hasLayer(obj.marker)) window.leafletMap.removeLayer(obj.marker);
          }
        } catch (e) { /* ignore */ }
      });

      // Build projects list for visible markers and refresh the filter panel
      try {
        const visibleNames = new Set();
        schoolMarkers.forEach(obj => { try { if (obj && obj.name && obj.marker && window.leafletMap.hasLayer(obj.marker)) visibleNames.add(String(obj.name).trim()); } catch(e){} });
        const projectsForVisible = (window.projectRows || []).filter(p => {
          const s = (p['Campus'] ?? p['School Name'] ?? p['Campus/School'] ?? '').toString().trim();
          return visibleNames.has(s);
        });
  // debug logging removed
  // Debug logging disabled by default. Set window.__debugDistrictFiltering = true to enable.
        const heading = selectedLegislativeDistrict ? `Legislative District ${selectedLegislativeDistrict}` : 'All Schools';
        try { showSidePanel(null, projectsForVisible, heading); } catch(e) {}
        try { if (typeof window.setFilterActive === 'function') window.setFilterActive(!!selectedLegislativeDistrict); else window.__filterActive = !!selectedLegislativeDistrict; } catch(e) {}
      } catch (e) { /* ignore */ }
    } catch (e) { /* ignore */ }
  }
  try { window.updateSchoolMarkersByDistrict = updateSchoolMarkersByDistrict; } catch(e) { /* ignore */ }

  // Support filtering markers by Attendance Area High School (string or null to clear)
  let selectedAttendanceHighSchool = null;
  function updateSchoolMarkersByAttendance(attendanceHS) {
    try {
      selectedAttendanceHighSchool = (attendanceHS == null) ? null : String(attendanceHS).trim();
      try { window.__selectedAttendanceHighSchool = selectedAttendanceHighSchool; } catch(e) {}
      const normalize = (v) => (v == null) ? '' : String(v).trim().toLowerCase();
      const schoolsInAttendance = new Set();
      // Helper: check if an attendance string (possibly comma-separated) contains the target token
      const matchesAttendanceString = (attStr, targetNorm) => {
        try {
          if (!attStr) return false;
          const raw = String(attStr).trim();
          if (!raw) return false;
          const low = raw.toLowerCase();
          if (low === 'n/a' || low === 'na' || low === 'none') return false;
          const parts = raw.split(',').map(p => String(p || '').trim().toLowerCase()).filter(Boolean);
          return parts.indexOf(targetNorm) !== -1;
        } catch (e) { return false; }
      };

      if (selectedAttendanceHighSchool) {
        try {
          const sel = String(selectedAttendanceHighSchool).trim();
          const selNorm = sel.toLowerCase();
          (window.schoolRows || []).forEach(s => {
            try {
              const raw = s['Attendance Area High School'] ?? s['AttendanceAreaHighSchool'] ?? '';
              const val = (raw == null) ? '' : String(raw).trim();
              if (!val) return;
              if (matchesAttendanceString(val, selNorm)) {
                schoolsInAttendance.add(normalize(s['School Name'] ?? s['Campus/School']));
              }
            } catch (e) { /* ignore row */ }
          });
        } catch (e) { /* ignore */ }
      }

      // Diagnostic: collect the set of matched school row names for debugging
      try {
        const matchedRows = [];
        const selNormForDiag = String(selectedAttendanceHighSchool || '').toLowerCase();
        (window.schoolRows || []).forEach(s => {
          try {
            const raw = s['Attendance Area High School'] ?? s['AttendanceAreaHighSchool'] ?? '';
            const val = (raw == null) ? '' : String(raw).trim();
            if (!val) return;
            if (matchesAttendanceString(val, selNormForDiag)) matchedRows.push(String(s['School Name'] ?? s['Campus/School']).trim());
          } catch (e) { }
        });
        // dedupe matchedRows for clarity
        const uniqueMatched = Array.from(new Set(matchedRows));
        try { window.__matchedAttendanceRows = uniqueMatched; } catch(e){}
      } catch (e) { /* ignore */ }

      // Show/hide markers: when attendance selected, show only schools in that attendance area
      // To avoid duplicate markers for the same school name (data may contain duplicates),
      // track which normalized names we've already added and skip subsequent markers.
      const addedNames = new Set();
      schoolMarkers.forEach(obj => {
        try {
          if (!(obj.marker instanceof L.Marker)) return;
          if (!window.leafletMap || !(window.leafletMap instanceof L.Map)) return;
          let inAttendance = true;
          if (selectedAttendanceHighSchool) {
            try {
              // prefer marker-attached property if available
              const markerAtt = (obj.attendanceHighSchool != null) ? obj.attendanceHighSchool : (obj.marker && obj.marker.__attendanceHighSchool != null ? obj.marker.__attendanceHighSchool : null);
              if (markerAtt != null) {
                const mAtt = String(markerAtt).trim();
                const mAttLow = mAtt.toLowerCase();
                if (!mAtt || mAttLow === 'n/a' || mAttLow === 'na' || mAttLow === 'none') {
                  inAttendance = false;
                } else {
                  // match against comma-separated marker-attached attendance
                  const parts = mAtt.split(',').map(p => String(p || '').trim().toLowerCase()).filter(Boolean);
                  inAttendance = parts.indexOf(String(selectedAttendanceHighSchool).trim().toLowerCase()) !== -1;
                }
              } else {
                inAttendance = schoolsInAttendance.has(normalize(obj.name));
              }
            } catch (e) { inAttendance = schoolsInAttendance.has(normalize(obj.name)); }
          } else {
            inAttendance = true;
          }

          if (inAttendance) {
            try {
              const norm = normalize(obj.name || obj.schoolId || '');
              if (addedNames.has(norm)) {
                // remove duplicate marker objects for same school name
                try { if (window.leafletMap.hasLayer(obj.marker)) window.leafletMap.removeLayer(obj.marker); } catch(e) {}
              } else {
                addedNames.add(norm);
                try { if (!window.leafletMap.hasLayer(obj.marker)) obj.marker.addTo(window.leafletMap); } catch(e) {}
              }
            } catch(e) { /* ignore dedupe errors */ }
          } else {
            if (window.leafletMap.hasLayer(obj.marker)) window.leafletMap.removeLayer(obj.marker);
          }
        } catch (e) { /* ignore */ }
      });

      // Refresh side panel for visible markers
      try {
        const visibleNames = new Set();
        schoolMarkers.forEach(obj => { try { if (obj && obj.name && obj.marker && window.leafletMap.hasLayer(obj.marker)) visibleNames.add(String(obj.name).trim()); } catch(e){} });
        const projectsForVisible = (window.projectRows || []).filter(p => {
          const s = (p['Campus'] ?? p['School Name'] ?? p['Campus/School'] ?? '').toString().trim();
          return visibleNames.has(s);
        });
        const heading = selectedAttendanceHighSchool ? `Attendance Area: ${selectedAttendanceHighSchool}` : 'All Schools';
        try { showSidePanel(null, projectsForVisible, heading); } catch(e) {}
        try { if (typeof window.setFilterActive === 'function') window.setFilterActive(!!selectedAttendanceHighSchool); else window.__filterActive = !!selectedAttendanceHighSchool; } catch(e) {}
      } catch (e) { /* ignore */ }
      try {
        // Developer debug: log marker counts (total vs visible after filter)
        const totalMarkers = Array.isArray(schoolMarkers) ? schoolMarkers.length : 0;
        const visibleCount = (Array.isArray(schoolMarkers) && window.leafletMap) ? schoolMarkers.reduce((acc, obj) => { try { return acc + ((obj && obj.marker && window.leafletMap.hasLayer(obj.marker)) ? 1 : 0); } catch(e){ return acc; } }, 0) : 0;
        // Also list matched row names and visible marker names for clarity
  const visibleNames = (Array.isArray(schoolMarkers) && window.leafletMap) ? schoolMarkers.reduce((acc, obj) => { try { if (obj && obj.marker && window.leafletMap.hasLayer(obj.marker)) acc.push(String(obj.name).trim()); } catch(e){} return acc; }, []) : [];
  const uniqueVisible = Array.from(new Set(visibleNames));
  try { window.__visibleMarkerNames = uniqueVisible; } catch(e){}
  // Debug logging removed. Diagnostics are still available on window.__matchedAttendanceRows
  // and window.__visibleMarkerNames if you need to inspect them interactively.
      } catch (e) { /* ignore logging errors */ }
    } catch (e) { /* ignore */ }
  }
  try { window.updateSchoolMarkersByAttendance = updateSchoolMarkersByAttendance; } catch(e) { /* ignore */ }

  // Do not call updateSchoolMarkers(null) here — markers will be added after geolocation permission is handled

  typeBtns.forEach(btn => {
    btn.addEventListener('click', function () {
      // Get announcement live region
      const liveRegion = document.getElementById('filterStatusAnnouncement');
      
      // Some button labels may contain newlines or extra whitespace (from layout)
      // normalize by collapsing whitespace to a single space and trimming.
      const raw = btn.getAttribute('data-type') || btn.textContent || '';
      const btnLabel = String(raw).replace(/\s+/g, ' ').trim();
      // Map display labels to canonical marker type codes used in schoolMarkers (if any)
      const labelToCode = {
        'Elementary Schools': 'ES',
        'Middle Schools': 'MS',
        'High Schools': 'HS',
        'Elementary': 'ES',
        'Middle': 'MS',
        'High': 'HS'
      };
      const code = labelToCode[btnLabel] || btnLabel;
      if (selectedType === code) {
        typeBtns.forEach(b => b.classList.remove('active'));
        typeBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
        selectedType = null;
        try { window.__selectedSchoolType = null; } catch (e) {}
        updateSchoolMarkers(null);
  try { showSidePanel(null, projectRows, 'All Schools'); } catch(e){}
  // If nearby radius is active, keep filter active (and Reset visible), otherwise clear
  try { if (typeof window.setFilterActive === 'function') window.setFilterActive(!!(window.__nearbyInfo && window.__nearbyInfo.lat != null)); else window.__filterActive = !!(window.__nearbyInfo && window.__nearbyInfo.lat != null); } catch (e) {}
        
        // Announce filter cleared
        try {
          if (liveRegion) {
            liveRegion.textContent = `${btnLabel} filter cleared, showing all schools`;
          }
        } catch (e) {}
      } else {
        typeBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        // update aria-pressed for accessibility
        typeBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        selectedType = code;
        try { window.__selectedSchoolType = code; } catch (e) {}
        updateSchoolMarkers(selectedType);
        try {
          const heading = `${btnLabel}`;
          try { if (typeof window.setFilterActive === 'function') window.setFilterActive(true); else window.__filterActive = true; } catch (e) {}
          showSidePanel(null, projectRows, heading);
        } catch(e){}
        // Reset visibility handled via setFilterActive
        
        // Announce filter applied
        try {
          if (liveRegion) {
            const visibleCount = schoolMarkers.filter(m => m.marker && window.leafletMap && window.leafletMap.hasLayer(m.marker)).length;
            liveRegion.textContent = `${btnLabel} filter applied, showing ${visibleCount} schools`;
          }
        } catch (e) {}
      }
    });
  });

  // Initialize the standalone search module (wires expand, suggestions, keyboard nav)
  try { initSearch(); } catch (e) { /* ignore */ }
  // Initialize dropdown behavior (separate module)
  try { initDropdowns('.map-filter-panel'); } catch (e) { /* ignore */ }
  // debug logging removed
});

// Debugging helpers: listen for pointerdown/click on hs-att-item globally to trace events
try {
  document.addEventListener('pointerdown', function (ev) {
    try {
      const el = ev.target && (typeof ev.target.closest === 'function' ? ev.target.closest('.hs-att-item') : null);
      if (!el) return;
  // debug logging removed
    } catch (e) {}
  });
  document.addEventListener('click', function (ev) {
    try {
      const el = ev.target && (typeof ev.target.closest === 'function' ? ev.target.closest('.hs-att-item') : null);
      if (!el) return;
  // debug logging removed
    } catch (e) {}
  });
} catch (e) { /* ignore */ }

// Popup content function (top-level)

// Accordion toggle function
window.toggleAccordion = function (btn) {
  const accordion = btn.closest('.accordion');
  const allHeaders = accordion.querySelectorAll('.accordion-header');
  const allBodies = accordion.querySelectorAll('.accordion-body');
  const body = btn.nextElementSibling;
  const isOpen = btn.classList.contains('active');
  // If already open, close all
  if (isOpen) {
    btn.classList.remove('active');
    body.classList.remove('open');
  } else {
    allHeaders.forEach(h => h.classList.remove('active'));
    allBodies.forEach(b => b.classList.remove('open'));
    btn.classList.add('active');
    body.classList.add('open');
  }
};


// Optionally, add a global click handler to close the side panel when overlay is clicked
window.closeSidePanel = function () {
  const sidePanel = document.getElementById('sidePanel');
  try {
    // Mark that the side panel was closed via the close-button (or via code calling closeSidePanel).
    // The offcanvas hidden handler will treat this as a signal to leave focus on the Back button
    // rather than auto-focusing the last clicked marker.
    try { window.__sidePanelClosedByCloseButton = true; } catch (e) { }
    const oc = bootstrap.Offcanvas.getInstance(sidePanel) || new bootstrap.Offcanvas(sidePanel);
    oc.hide();
  } catch (err) {
    // fallback
    sidePanel.classList.remove('visible');
  // fallback: do NOT refresh the map filter panel here. Reset must be explicit.
    // no overlay fallback
  }
};

// GeoJSON loading moved inside DOMContentLoaded to use the initialized map instance
